/* FdFarmerSupplierForm.tsx
   – polished layout to match Farm / Customer forms */

import React, {useEffect} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import {Controller, useFieldArray, useForm} from 'react-hook-form';

/* ───────── types ───────── */
export type LinkInput = {
  id?: number;
  supplierId: number | '' | string;
  productIds: number[];
  otherText?: string;
};
export type Submission = {links: LinkInput[]};

export const OTHER_SUPPLIER_ID = 'OTHER';

/* ───────── component ───────── */
export function FdFarmerSupplierForm(props: {
  suppliers: {
    id: string;
    name: string;
    products: {id: number; name: string}[];
  }[];
  defaultLinks: LinkInput[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (d: Submission) => void;
  onCancel: () => void;
}) {
  const {control, handleSubmit, watch, setValue, reset} = useForm<Submission>({
    defaultValues: {links: props.defaultLinks},
  });
  const {fields, append, remove, replace} = useFieldArray({
    control,
    name: 'links',
  });
  const linksWatch = watch('links');

  // Reset form when defaultLinks change (e.g., when loading "Other" entries from localStorage)
  useEffect(() => {
    if (props.defaultLinks && props.defaultLinks.length >= 0) {
      replace(props.defaultLinks);
      reset({links: props.defaultLinks}, {keepDefaultValues: false});
    }
  }, [JSON.stringify(props.defaultLinks), replace, reset]);

  return (
    <div className="generic-form-container fd-supplier-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(props.onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2">Primary suppliers</Typography>
            </Grid>

            {fields.map((field, idx) => {
              const supplierId = linksWatch?.[idx]?.supplierId;
              const isOther = supplierId === OTHER_SUPPLIER_ID;
              const selectedSupplier = props.suppliers.find(
                (s) => s.id === String(supplierId),
              );
              const productOptions = selectedSupplier?.products ?? [];
              const disabled = !selectedSupplier || productOptions.length === 0;

              return (
                <React.Fragment key={field.id}>
                  {/* supplier */}
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={`links.${idx}.supplierId`}
                      control={control}
                      rules={{required: true}}
                      render={({field: {onChange, value, ...field}}) => (
                        <TextField
                          {...field}
                          select
                          label="Supplier"
                          variant="outlined"
                          fullWidth
                          value={value || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            onChange(newValue);
                            setValue(`links.${idx}.productIds`, []); // reset products
                            if (newValue !== OTHER_SUPPLIER_ID) {
                              setValue(`links.${idx}.otherText`, ''); // reset other text
                            }
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          SelectProps={{
                            displayEmpty: true,
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select supplier</em>
                          </MenuItem>
                          {props.suppliers.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name}
                            </MenuItem>
                          ))}
                          <MenuItem value={OTHER_SUPPLIER_ID}>Other</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>

                  {/* products or other text */}
                  <Grid item xs={12} sm={6}>
                    {isOther ? (
                      <Controller
                        name={`links.${idx}.otherText`}
                        control={control}
                        rules={{
                          required: 'Please specify supplier and products',
                        }}
                        render={({
                          field: {onChange, value, ...field},
                          fieldState: {error},
                        }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            label="Specify supplier and products"
                            fullWidth
                            multiline
                            rows={3}
                            value={value || ''}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message || null}
                            placeholder="e.g., ABC Seeds - Corn varieties, Wheat seeds"
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        name={`links.${idx}.productIds`}
                        control={control}
                        render={({field}) => (
                          <Autocomplete
                            multiple
                            options={productOptions}
                            getOptionLabel={(o) => o.name}
                            value={productOptions.filter((p) =>
                              field.value.includes(p.id),
                            )}
                            onChange={(_, v) =>
                              field.onChange(v.map((x) => x.id))
                            }
                            disableCloseOnSelect
                            disabled={disabled}
                            renderTags={(value, getTagProps) =>
                              value.map((opt, i) => (
                                <Chip
                                  {...getTagProps({index: i})}
                                  key={opt.id}
                                  label={opt.name}
                                  color="primary"
                                  size="small"
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                label="Products"
                                fullWidth
                              />
                            )}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  {/* remove */}
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    style={{display: 'flex', alignItems: 'center'}}
                  >
                    <Button onClick={() => remove(idx)}>Remove</Button>
                  </Grid>
                </React.Fragment>
              );
            })}

            {/* add button */}
            <Grid item xs={12}>
              <Button
                onClick={() =>
                  append({supplierId: '', productIds: [], otherText: ''})
                }
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {/* footer actions */}
          <Grid container spacing={2} style={{marginTop: '1rem'}}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={props.onCancel}
                disabled={props.isLoading}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={props.isLoading}
              >
                {props.isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
